<template>
  <div class="flex-child">
    <div class="body-container">
      <header>
        <h2>AI Knowledge Base Content Processor</h2>
        <div class="header-actions">
            <button 
              @click="openConfigModal" 
              class="config-btn"
              title="AI Configuration">
              <i class="fas fa-cog"></i>
            </button>
            <div class="genesys-status">
              <span class="status-indicator" :class="genesysStatus.connected ? 'connected' : 'disconnected'">
                {{ genesysStatus.connected ? '🟢 Genesys Connected' : '🔴 Genesys Disconnected' }}
              </span>
            </div>
        </div>
      </header>

      <!-- AI Configuration Modal -->
      <dialog class="dialog-ai-config" :open="showConfigModal">
        <div class="dialog-backdrop" @click="closeConfigModal"></div>
        <div class="dialog-body">
          <button type="button" class="close" @click="closeConfigModal">
            <span>&times;</span>
          </button>
          <form @submit.prevent="saveAIConfiguration">
            <h4 class="dialog-header">AI Configuration</h4>

            <div class="dialog-grid-container">
              <label for="bedrock-api-key">API Key</label>
              <BaseInput
                id="bedrock-api-key"
                v-model="aiConfig.bedrock_api_key"
                placeholder=""
                type="password"
                required
                style="margin-bottom: 20px" />

              <label for="genesys-client-id">Genesys Client ID</label>
              <BaseInput
                id="genesys-client-id"
                v-model="aiConfig.genesys_client_id"
                placeholder=""
                type="text"
                required
                style="margin-bottom: 20px" />

              <label for="genesys-client-secret">Genesys Client Secret</label>
              <BaseInput
                id="genesys-client-secret"
                v-model="aiConfig.genesys_client_secret"
                placeholder=""
                type="password"
                required
                style="margin-bottom: 20px" />

              <label for="genesys-environment">Genesys Environment</label>
              <select 
                id="genesys-environment"
                v-model="aiConfig.genesys_environment"
                class="form-control"
                style="margin-bottom: 20px">
                <option value="usw2.pure.cloud">usw2.pure.cloud</option>
                <option value="mypurecloud.com">mypurecloud.com</option>
                <option value="usw2.pure.cloud">usw2.pure.cloud</option>
                <option value="use2.us-gov-pure.cloud">use2.us-gov-pure.cloud</option>
                <option value="cac1.pure.cloud">cac1.pure.cloud</option>
                <option value="mypurecloud.ie">mypurecloud.ie</option>
                <option value="euw2.pure.cloud">euw2.pure.cloud</option>
                <option value="mypurecloud.de">mypurecloud.de</option>
                <option value="aps1.pure.cloud">aps1.pure.cloud</option>
                <option value="mypurecloud.jp">mypurecloud.jp</option>
                <option value="apne2.pure.cloud">apne2.pure.cloud</option>
                <option value="mypurecloud.com.au">mypurecloud.com.au</option>
                <option value="sae1.pure.cloud">sae1.pure.cloud</option>
              </select>
            </div>

            <div class="config-status" v-if="configStatus">
              <div :class="['status-message', configStatus.type]">
                {{ configStatus.message }}
              </div>
            </div>

            <div class="button-group" style="margin-top: 20px">
              <ButtonSubmit 
                label="Save Configuration"
                :disabled="savingConfig" />
                <ButtonSubmit 
                label="Test Connection"
                :disabled="testingConfig"
                @click="testConfiguration" />
            
              <ButtonCancel
                label="Cancel"
                type="button"
                @click="closeConfigModal" />
            </div>
          </form>
        </div>
      </dialog>

      <div id="subheader" v-if="processedData" class="member-info subheader-aligned">
        <div class="info-item">
          <span class="info-label">Source Type:</span>
          <span class="info-value">{{ getSourceTypeLabel() }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Output Format:</span>
          <span class="info-value">Genesys KB JSON</span>
        </div>
        <div class="info-item" v-if="processedData?.json?.documents">
          <span class="info-label">Total Articles:</span>
          <span class="info-value">{{ processedData.json.documents.length }}</span>
        </div>
        <div class="info-item" v-if="getContentLength()">
          <span class="info-label">Content Length:</span>
          <span class="info-value">{{ getContentLength() }} chars</span>
        </div>
        <div class="info-item" v-if="approvedArticles.length > 0">
          <span class="info-label">Approved:</span>
          <span class="info-value approved-count">{{ approvedArticles.length }}</span>
        </div>
        <div class="info-item" v-if="selectedKnowledgeBase">
          <span class="info-label">Target KB:</span>
          <span class="info-value">{{ selectedKnowledgeBase.name }}</span>
        </div>
        <div class="info-item" v-if="processedData?.import_result">
          <span class="info-label">Status:</span>
          <span class="info-value import-success">✅ Imported</span>
        </div>
      </div>

      <div id="centerdiv" :class="{ 'article-editor-mode': showArticleEditor }">
        <!-- Left Column - Configuration OR Article Basic Info Editor -->
        <div class="containerdiv" 
             :class="{ 'editor-left-column': showArticleEditor }"
             id="containerdiv1">
          
          <!-- Original configuration content when NOT editing -->
          <div v-if="!showArticleEditor">
            <!-- Content Source Selection -->
            <div class="source-section">
              <h4>Content Source</h4>
              <div class="radiolist">
                <div style="display: flex; margin-top: 13px;">
                  <input type="radio" id="source_url" value="url" v-model="activeSource">
                  <label for="source_url">Website URL</label>
                </div>
                <div style="display: flex; margin-top: 13px;">
                  <input type="radio" id="source_file" value="file" v-model="activeSource">
                  <label for="source_file">Upload Documents</label>
                </div>
                <div style="display: flex; margin-top: 13px;">
                  <input type="radio" id="source_text" value="text" v-model="activeSource">
                  <label for="source_text">Paste Content</label>
                </div>
              </div>

              <!-- URL Input -->
              <div v-if="activeSource === 'url'" class="source-content">
                <BaseInput
                  type="url"
                  v-model="urlInput"
                  placeholder="https://example.com/article-or-documentation"
                  class="form-control"
                  style="padding-left: 10px; font-size: 16px; margin-top: 3px; min-width: 400px" />
                <div class="source-options-row">
                  <BaseInput
                    type="number"
                    id="link-limit"
                    v-model="linkLimit"
                    placeholder="0"
                    class="form-control"
                    style="width: 80px; padding-left: 10px; font-size: 16px;" />
                  <label for="link-limit">Depth limit</label>
                </div>
                <div class="field-help" style="margin-top: 4px; font-size: 12px; color: #6b7280;">
                  Depth limit 0 = scrape only the inputted page
                </div>
                <div class="field-help" :style="urlValidationStyle">
                  {{ urlValidationText }}
                </div>
              </div>

              <!-- File import -->
              <div v-if="activeSource === 'file'" class="source-content">
                <input
                  type="file"
                  ref="fileInput"
                  class="file-input form-control"
                  accept=".pdf,.docx,.doc,.txt,.html,.md, .json"
                  multiple
                  @change="handleFileChange"
                  style="font-size: 16px; margin-top: 3px;" />
                <div class="field-help">{{ fileValidationText }}</div>
              </div>

              <!-- Text Input -->
              <div v-if="activeSource === 'text'" class="source-content">
                <textarea
                  v-model="textInput"
                  class="form-control-textarea"
                  placeholder="Paste your content here..."
                  style="font-size: 16px; min-width: 97%; max-width: 97%; min-height: 200px;"
                  @input="autoResizeTextarea"></textarea>
                <div class="field-help">Direct text input for immediate processing</div>
              </div>
            </div>


            <!-- Action Buttons -->
            <ButtonSubmit 
              style="margin-top: 13.5px" 
              label="Process Content"
              :disabled="isProcessing || !canProcess"
              @click="processContent" />
            <ButtonCancel 
              style="margin-top: 10px" 
              label="Reset" 
              @click="resetForm"
              :disabled="isProcessing" />
          </div>

          <!-- Article basic info editor when editing -->
          <div v-if="showArticleEditor">
            <div class="article-editor">
              <div class="editor-header">
                <h4>Edit Article</h4>
                <BaseButton 
                  @click="closeArticleEditor"
                  class="btn-secondary btn-rounded">
                  Back to Articles
                </BaseButton>
              </div>

              <!-- Article Title -->
              <div class="edit-field">
                <label>Article Title</label>
                <BaseInput
                  v-model="editingArticle.draft.title"
                  class="form-control"
                  placeholder="Enter article title..."
                  style="font-size: 16px; margin-top: 3px;" />
              </div>

              <!-- Category Selection -->
              <div class="edit-field">
                <label>Category</label>
                <select 
                  v-model="editingArticle.draft.category.name"
                  class="form-control"
                  style="font-size: 16px; margin-top: 3px;">
                  <option value="Account">Account</option>
                  <option value="Digital Banking">Digital Banking</option>
                  <option value="Loan">Loan</option>
                  <option value="General Information">General Information</option>
                  <option value="Cards">Cards</option>
                  <option value="Branches">Branches</option>
                  <option value="Support">Support</option>
                  <option value="Services">Services</option>
                </select>
              </div>

              <!-- Status - Draft/Published -->
              <div class="edit-field">
                <label>Publication Status</label>
                <div class="status-options">
                  <label class="radio-option">
                    <input 
                      type="radio" 
                      value="Published" 
                      v-model="editingArticleStatus"
                      @change="updateArticleStatus">
                    Published
                  </label>
                  <label class="radio-option">
                    <input 
                      type="radio" 
                      value="Draft" 
                      v-model="editingArticleStatus"
                      @change="updateArticleStatus">
                    Draft
                  </label>
                </div>
              </div>

              <!-- Visibility -->
              <div class="edit-field">
                <div class="option-group">
                  <input 
                    type="checkbox" 
                    id="article-visible" 
                    v-model="editingArticle.draft.visible" />
                  <label for="article-visible">Article is visible</label>
                </div>
              </div>

              <!-- Labels -->
              <div class="edit-field">
                <label>Labels (comma-separated)</label>
                <BaseInput
                  v-model="labelsText"
                  class="form-control"
                  placeholder="Payment, Account, Help..."
                  style="font-size: 16px; margin-top: 3px;"
                  @input="updateLabels" />
              </div>

              <!-- Current Labels Display -->
              <div class="edit-field">
                <label>Current Labels</label>
                <div class="current-labels-display">
                  <span 
                    v-for="label in editingArticle.draft.labels" 
                    :key="label.name"
                    class="current-label-tag">
                    {{ label.name }}
                  </span>
                  <span v-if="!editingArticle.draft.labels?.length" class="no-labels">
                    No labels assigned
                  </span>
                </div>
              </div>

              <!-- Alternative Questions -->
              <div class="edit-field">
                <label>Alternative Questions</label>
                <div class="alternatives-list">
                  <div 
                    v-for="(alt, index) in editingArticle.draft.alternatives" 
                    :key="index"
                    class="alternative-item">
                    <BaseInput
                      v-model="alt.phrase"
                      class="form-control"
                      placeholder="Alternative question..."
                      style="flex: 1; font-size: 14px;" />
                    <BaseButton 
                      @click="removeAlternative(index)"
                      class="btn-danger-small btn-rounded">
                      Remove
                    </BaseButton>
                  </div>
                  <BaseButton 
                    @click="addAlternative"
                    class="btn-secondary btn-rounded">
                    Add Alternative
                  </BaseButton>
                </div>
              </div>

              <!-- Save/Cancel Buttons -->
              <div class="editor-actions">
                <ButtonSubmit 
                  label="Save Changes"
                  @click="saveArticleChanges" />
                <BaseButton 
                  @click="closeArticleEditor"
                  class="btn-secondary btn-rounded">
                  Cancel
                </BaseButton>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Article Content Editor (when editing) -->
        <div class="containerdiv member-info editor-content-column" 
             id="containerdiv2" 
             v-if="showArticleEditor">
          <div class="member-info-content">
            <div class="content-editor">
              <h4>Article Content</h4>
              
              <!-- Content Blocks Editor -->
              <div class="wysiwyg-editor-container">
                    <QuillEditor
                      ref="quillEditor"
                      v-model:content="wysiwygContent"
                      :options="quillOptions"
                      content-type="html"
                      class="wysiwyg-editor"
                      @ready="onEditorReady"
                    />
                  </div>
                
            </div>
          </div>
        </div>

        <!-- Center Column - Progress/Status/Loading -->
        <div class="containerdiv member-info processing-section" id="containerdiv2" v-if="(isProcessing || processedData) && !showArticleEditor">
          <div class="member-info-content">
            <!-- Live Progress Display -->
            <div v-if="isProcessing" class="progress-container">
              <h4>Processing Status</h4>
              
              <!-- Progress Bar -->
              <div v-if="progressInfo.total_steps" class="progress-bar-container">
                <div class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ width: progressPercentage + '%' }">
                  </div>
                </div>
                <div class="progress-text">
                  {{ progressInfo.current_step || 0 }} / {{ progressInfo.total_steps }} steps
                </div>
              </div>

              <!-- Current Status -->
              <div class="current-status">
                <div class="status-message">{{ currentStatus }}</div>
              </div>

              <!-- Progress Log -->
              <div class="progress-log">
                <h5>Progress Log:</h5>
                <div class="log-container">
                  <div 
                    v-for="(log, index) in progressLog" 
                    :key="index" 
                    class="log-entry"
                    :class="getLogEntryClass(log)">
                    <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
                    <span class="log-message">{{ log.message }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Results Summary (when complete) -->
            <div v-else-if="processedData" class="results-summary">
              <h4>Processing Complete</h4>
              
              <!-- Status Messages -->
              <div v-if="successMessage" class="status-message success">
                {{ successMessage }}
              </div>
              <div v-if="errorMessage" class="status-message error">
                {{ errorMessage }}
              </div>

              <!-- import Status -->
              <div v-if="processedData.import_result" class="import-status-section">
                <div class="import-success-message">
                   Successfully imported {{ importedApprovedCount || processedData.json?.documents?.length || 0 }} articles to Knowledge Base!
                <br>
                 
                </div>
              </div>

              <div v-if="processedData.import_error" class="import-error-section">
                <div class="import-error-message">
                  Import failed: {{ processedData.import_error }}
                </div>
              </div>


              <!-- Knowledge Base Selection for import -->
              <div v-if="approvedArticles.length > 0 && !processedData.import_result" class="kb-import-section">
                <h5>Import Approved Articles to Knowledge Base</h5>
                
                <div class="approved-articles-info">
                  <span class="approved-count-display">
                    {{ approvedArticles.length }} article{{ approvedArticles.length !== 1 ? 's' : '' }} approved for import
                  </span>
                </div>
                
                <div class="kb-selection">
                  <label for="import-kb-select">Select Knowledge Base:</label>
                  <select 
                    id="import-kb-select"
                    v-model="selectedKnowledgeBase"
                    class="form-control kb-dropdown"
                    style="font-size: 16px; margin-top: 8px;"
                    :disabled="loadingKnowledgeBases">
                    <option :value="null">{{ loadingKnowledgeBases ? 'Loading...' : 'Select Knowledge Base' }}</option>
                    <option 
                      v-for="kb in knowledgeBases" 
                      :key="kb.id" 
                      :value="kb">
                      {{ kb.name }} ({{ kb.articleCount }} articles)
                    </option>
                  </select>
                </div>

                <div class="kb-actions" style="margin-top: 12px;">
                  <BaseButton 
                    @click="loadKnowledgeBases"
                    :disabled="loadingKnowledgeBases"
                    class="btn-secondary btn-rounded"
                    style="margin-right: 8px;">
                    {{ loadingKnowledgeBases ? 'Loading...' : 'Refresh KB List' }}
                  </BaseButton>
                  
                  <BaseButton 
                    v-if="selectedKnowledgeBase" 
                    @click="importApprovedArticles" 
                    :disabled="importingManually"
                    class="btn-import btn-rounded">
                    {{ importingManually ? 'Importing...' : `Import ${approvedArticles.length} Approved Articles` }}
                  </BaseButton>
                </div>
              </div>

              <!-- No Approved Articles Warning -->
              <div v-else-if="processedData.json?.documents?.length > 0 && approvedArticles.length === 0 && !processedData.import_result" class="no-approved-warning">
                <div class="warning-message">
                  ⚠️ No articles have been approved for import. Please review and approve articles in the table below.
                </div>
              </div>

              <!-- Original import Section (fallback for when no approval system) -->
              <div v-else-if="processedData.json && !processedData.import_result && processedData.json?.documents?.length > 0" class="kb-import-section">
                <h5>Import to Knowledge Base</h5>
                
                <div class="kb-selection">
                  <label for="import-kb-select">Select Knowledge Base:</label>
                  <select 
                    id="import-kb-select"
                    v-model="selectedKnowledgeBase"
                    class="form-control kb-dropdown"
                    style="font-size: 16px; margin-top: 8px;"
                    :disabled="loadingKnowledgeBases">
                    <option :value="null">{{ loadingKnowledgeBases ? 'Loading...' : 'Select Knowledge Base' }}</option>
                    <option 
                      v-for="kb in knowledgeBases" 
                      :key="kb.id" 
                      :value="kb">
                      {{ kb.name }} ({{ kb.articleCount }} articles)
                    </option>
                  </select>
                </div>

                <div class="kb-actions" style="margin-top: 12px;">
                  <BaseButton 
                    @click="loadKnowledgeBases"
                    :disabled="loadingKnowledgeBases"
                    class="btn-secondary btn-rounded"
                    style="margin-right: 8px;">
                    {{ loadingKnowledgeBases ? 'Loading...' : 'Refresh KB List' }}
                  </BaseButton>
                  
                  <BaseButton 
                    v-if="selectedKnowledgeBase" 
                    @click="importToKnowledgeBase" 
                    :disabled="importingManually"
                    class="btn-import btn-rounded">
                    {{ importingManually ? 'importing...' : `import to ${selectedKnowledgeBase.name}` }}
                  </BaseButton>
                </div>
              </div>

              <!-- Download JSON -->
              <!-- <div class="download-section" style="margin-top: 16px;">
                <BaseButton @click="downloadJson" class="btn-submit btn-rounded">
                  Download All Articles JSON
                </BaseButton>
                <BaseButton 
                  v-if="approvedArticles.length > 0"
                  @click="downloadApprovedJson" 
                  class="btn-approved-download btn-rounded"
                  style="margin-left: 8px;">
                  Download Approved Only ({{ approvedArticles.length }})
                </BaseButton>
              </div> -->
            </div>
          </div>
        </div>

        <!-- Center Column - Getting Started (initial state) -->
        <div class="containerdiv member-info" id="containerdiv2" v-if="!isProcessing && !processedData && !showArticleEditor">
          <div class="member-info-content">
            <h4>Get Started</h4>
            <div class="getting-started">
              <div class="step-item">
                <span class="step-number">1</span>
                <div class="step-content">
                  <div class="step-title">Choose Content Source</div>
                  <div class="step-description">Select URL, import files, or paste text directly</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">2</span>
                <div class="step-content">
                  <div class="step-title">Configure Options</div>
                  <div class="step-description">Set processing preferences and output format</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">3</span>
                <div class="step-content">
                  <div class="step-title">Process Content</div>
                  <div class="step-description">Click "Process Content" to see real-time progress</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">4</span>
                <div class="step-content">
                  <div class="step-title">Review & Approve</div>
                  <div class="step-description">Review generated articles and approve for import</div>
                </div>
              </div>
              <div class="step-item">
                <span class="step-number">5</span>
                <div class="step-content">
                  <div class="step-title">Import Approved</div>
                  <div class="step-description">Import only approved articles to Knowledge Base</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Analysis and import Results -->
        <div class="containerdiv member-info analysis-section-container" id="containerdiv3" v-if="processedData && !showArticleEditor">
          <div class="member-info-content">
            <!-- Genesys import Info -->
            <div v-if="processedData.import_result || processedData.import_error" class="import-info-section" style="margin-bottom: 20px;">
              <h4>import Results</h4>
              <div v-if="processedData.import_result" class="import-details success">
                <div class="import-status">Successfully Imported to Knowledge Base</div>
                <div class="import-detail-item">
                  <strong>Knowledge Base:</strong> {{ selectedKnowledgeBase?.name }}
                </div>
                <div class="import-detail-item">
                  <strong>Articles imported:</strong> {{ importedApprovedCount || processedData.json?.documents?.length || 0 }}
                </div>
              </div>
              <div v-if="processedData.import_error" class="import-details error">
                <div class="import-status">❌ Import failed</div>
                <div class="import-detail-item">
                  <strong>Error:</strong> {{ processedData.import_error }}
                </div>
              </div>
            </div>

            <!-- AI Analysis -->
            <div v-if="processedData.analysis" class="analysis-section" style="margin-bottom: 20px;">
              <h4>Content Analysis</h4>
              <div class="analysis-content">{{ processedData.analysis }}</div>
            </div>

            <!-- URL List -->
            <div v-if="crawledUrls.length > 0" class="urls-section">
              <h4>Processed URLs ({{ crawledUrls.length }})</h4>
              <div class="urls-list-container">
                <div class="urls-list">
                  <div v-for="(url, index) in crawledUrls" :key="url" class="url-item">
                    <span class="url-number">{{ index + 1 }}</span>
                    <a :href="url" target="_blank" class="url-link">{{ formatUrl(url) }}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Knowledge Base Info (when no data) -->
        <div class="containerdiv member-info" id="containerdiv3" v-if="!processedData && !showArticleEditor">
          <div class="member-info-content">
            <h4>Genesys Integration</h4>
            <div class="feature-list">
              <div class="feature-item">
                <div class="feature-text">Article approval workflow</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Import only approved articles</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Bulk approval/rejection actions</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Direct import to Genesys Knowledge Bases</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Real-time import job tracking</div>
              </div>
              <div class="feature-item">
                <div class="feature-text">Article editing before import</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Articles Management Section with Approval (Full Width at Bottom) -->
      <div v-if="processedData?.json?.documents?.length && !showArticleEditor" class="articles-full-section">
        <div class="articles-section-wrapper">
          <div class="articles-section">
            <div class="articles-header">
              <h4>Generated Articles ({{ processedData.json.documents.length }})</h4>
              <div class="articles-header-actions">
                <div class="approval-summary-mini">
                  <span class="mini-stat approved">{{ approvedArticles.length }}</span>
                  <span class="mini-stat pending">{{ pendingArticles.length }}</span>
                  <span class="mini-stat rejected">{{ rejectedArticles.length }}</span>
                </div>
              </div>
            </div>
            
            
             <!-- Approval Actions Bar -->
              <div class="approval-actions-bar">
                <div class="selection-info">
                  <span v-if="selectedArticles.length > 0">
                    {{ selectedArticles.length }} of {{ processedData.json.documents.length }} articles selected
                  </span>
                  <span v-else>
                    Select articles to approve or reject
                  </span>
                </div>
                
                <div class="approval-actions">
                  <BaseButton 
                    @click="approveSelectedArticles"
                    :disabled="selectedArticles.length === 0"
                    class="btn-approve btn-rounded">
                    Approve Selected ({{ selectedArticles.length }})
                  </BaseButton>
                  <BaseButton 
                    @click="rejectSelectedArticles"
                    :disabled="selectedArticles.length === 0"
                    class="btn-reject btn-rounded">
                    Reject Selected
                  </BaseButton>
                  <BaseButton 
                    @click="resetSelectedApprovals"
                    :disabled="selectedArticles.length === 0"
                    class="btn-reset btn-rounded">
                    Reset Selected
                  </BaseButton>
                </div>
              </div>
              <!-- Articles Table -->
            <div class="articles-table-container">
              <table class="articles-table">
                <thead>
                  <tr>
                    <th class="checkbox-column">
                            <div class="checkbox-header-wrapper">
                              <input 
                                type="checkbox" 
                                :checked="allArticlesSelected"
                                @change="toggleAllArticles"
                                class="select-all-checkbox"
                                title="Select/Deselect All">
                            </div>
                          </th>
                    <th class="approval-column">Approval</th>
                    <th class="title-column">Title</th>
                    <th class="category-column">Category</th>
                    <th class="status-column">Status</th>
                    <th class="visible-column">Visible</th>
                    <th class="labels-column">Labels</th>
                    <th class="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(article, index) in processedData.json.documents" 
                    :key="index"
                    class="article-row"
                    :class="getArticleRowClass(index)">
                    
                  <td class="checkbox-cell">
                    <input 
                      type="checkbox" 
                      :checked="selectedArticles.includes(index)"
                      @change="toggleArticleSelection(index)"
                      class="article-checkbox">
                  </td>

                    <!-- Approval Column -->
                    <td class="approval-status-cell">
                      <span :class="['status-badge', 'approval-status-badge', getApprovalStatus(index)]">
                        {{ getApprovalStatusText(index) }}
                      </span>
                    </td>
                    
                    <td class="title-cell">
                      <div class="article-title">{{ getCurrentArticleData(article).title }}</div>
                      <div class="article-preview">{{ getArticlePreview(article) }}</div>
                    </td>
                    <td class="category-cell">
                      <span class="category-badge rectangle-badge">{{ getCurrentArticleData(article).category.name }}</span>
                    </td>
                    <td class="status-cell">
                      <span class="status-badge rectangle-badge" :class="getArticleStatusClass(article)">
                        {{ getArticleStatus(article) }}
                      </span>
                    </td>
                    <td class="visible-cell">
                      <div class="visibility-toggle">
                        <input 
                          type="checkbox" 
                          :checked="getCurrentArticleData(article).visible"
                          @change="toggleArticleVisibility(index, $event.target.checked)"
                          class="visibility-checkbox">
                        <span class="visibility-label">{{ getCurrentArticleData(article).visible ? 'Visible' : 'Hidden' }}</span>
                      </div>
                    </td>
                    <td class="labels-cell">
                      <div class="labels-list">
                        <span 
                          v-for="label in getCurrentArticleData(article).labels" 
                          :key="label.name"
                          class="label-tag rectangle-badge">
                          {{ label.name }}
                        </span>
                      </div>
                    </td>
                    <td class="actions-cell">
                        <div class="action-buttons">
                          <button 
                            @click="editArticle(index)"
                            class="action-icon-btn"
                            :class="{ 'disabled': getApprovalStatus(index) === 'approved' }"
                            :disabled="getApprovalStatus(index) === 'approved'"
                            :title="getApprovalStatus(index) === 'approved' ? 'Cannot edit approved articles' : 'Edit Article'">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button 
                            @click="deleteArticle(index)"
                            class="action-icon-btn delete-btn"
                            title="Delete Article">
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                   
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { ref, computed, onMounted, nextTick } from 'vue'
import BaseButton from "./UI/BaseButton.vue"
import BaseInput from "./UI/BaseInput.vue"
import ButtonSubmit from "./ButtonSubmit.vue"
import ButtonCancel from "./ButtonCancel.vue"
import { flashMessage } from "@/functions"

// Reactive data
// Add these to your existing ref declarations
const wysiwygContent = ref('')
// Image upload handler for Quill
function imageHandler() {
  const input = document.createElement('input')
  input.setAttribute('type', 'file')
  input.setAttribute('accept', 'image/*')
  input.click()

  input.onchange = async () => {
    const file = input.files[0]
    if (file) {
      try {
        const company = localStorage.getItem("companyID") || "default"
        const formData = new FormData()
        formData.append('file', file)
        formData.append('company', company)

        const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/upload-image`, {
          method: 'POST',
          body: formData
        })

        if (response.ok) {
          const result = await response.json()
          const quill = quillEditor.value.getQuill()
          const range = quill.getSelection(true)
          quill.insertEmbed(range.index, 'image', result.url)
          quill.setSelection(range.index + 1)
        } else {
          console.error('Image upload failed')
          alert('Failed to upload image. Please try again.')
        }
      } catch (error) {
        console.error('Error uploading image:', error)
        alert('Error uploading image. Please try again.')
      }
    }
  }
}

const quillOptions = ref({
  theme: 'snow',
  modules: {
    toolbar: {
      container: [
        [{ 'header': [3, 4, false] }], // This will show "Header 3", "Header 4", "Normal"
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  },
  formats: ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link', 'image'], // Specify allowed formats
  placeholder: 'Write your article content here...'
})
const quillEditor = ref(null)
const editorInstance = ref(null)
const pendingContent = ref('')
const activeSource = ref('url')
const urlInput = ref('')
const textInput = ref('')
const selectedFiles = ref([])
const linkLimit = ref(0)
const includeAnalysis = ref(true)
const optimizeTokens = ref(true)
const structuredOutput = ref(true)

// Genesys-specific data
const knowledgeBases = ref([])
const selectedKnowledgeBase = ref(null)
const loadingKnowledgeBases = ref(false)
const importingManually = ref(false)
const genesysStatus = ref({ connected: false })

// Processing states
const isProcessing = ref(false)
const processedData = ref(null)
const successMessage = ref('')
const errorMessage = ref('')

// Article approval system - NEW
const articleApprovalStates = ref({}) // { articleIndex: 'approved' | 'rejected' | 'pending' }
const importedApprovedCount = ref(0)
const selectedArticles = ref([]) 

// Article editing states
const showArticleEditor = ref(false)
const editingArticle = ref(null)
const editingArticleIndex = ref(-1)
const editingArticleStatus = ref('Published')
const labelsText = ref('')

// Progress tracking
const currentStatus = ref('')
const progressInfo = ref({
  current_step: null,
  total_steps: null
})
const progressLog = ref([])

// Template refs
const fileInput = ref(null)

// Chunk buffer for large data
const chunkBuffer = ref({
  raw_content: null,
  analysis: null,
  documents: [],
  categories: [],
  labels: [],
  contexts: [],
  version: 2,
  isChunked: false
})

//Configuration
const showConfigModal = ref(false)
const savingConfig = ref(false)
const testingConfig = ref(false)
const configStatus = ref(null)

const aiConfig = ref({
  bedrock_api_key: '',
  genesys_client_id: '',
  genesys_client_secret: '',
  genesys_environment: 'usw2.pure.cloud'
})

// Approval system computed properties - NEW
const approvedArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    articleApprovalStates.value[index] === 'approved'
  )
})

const pendingArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    !articleApprovalStates.value[index] || articleApprovalStates.value[index] === 'pending'
  )
})

const rejectedArticles = computed(() => {
  if (!processedData.value?.json?.documents) return []
  return processedData.value.json.documents.filter((_, index) => 
    articleApprovalStates.value[index] === 'rejected'
  )
})

// Existing computed properties
const canProcess = computed(() => {
  switch (activeSource.value) {
    case 'url':
      return urlInput.value.trim() !== ''
    case 'file':
      return selectedFiles.value.length > 0
    case 'text':
      return textInput.value.trim() !== ''
    default:
      return false
  }
})

const urlValidationStyle = computed(() => {
  const url = urlInput.value.trim()
  if (!url) return { color: '#6b7280' }
  if (url && !url.startsWith('http')) return { color: '#dc2626' }
  return { color: '#059669' }
})

const urlValidationText = computed(() => {
  const url = urlInput.value.trim()
  if (!url) return 'Enter a webpage URL to extract content'
  if (url && !url.startsWith('http')) return 'URL must start with http:// or https://'
  return 'Ready to process this URL'
})

const fileValidationText = computed(() => {
  if (selectedFiles.value.length === 0) {
    return 'Supported: PDF, Word, TXT, HTML, Markdown, JSON, CSV, XLSX'
  }
  const fileNames = selectedFiles.value.map(f => f.name).join(', ')
  return `Selected: ${fileNames}`
})

const crawledUrls = computed(() => {
  return processedData.value?.raw_content?.crawled_urls || []
})

const progressPercentage = computed(() => {
  if (!progressInfo.value.total_steps || !progressInfo.value.current_step) return 0
  return Math.round((progressInfo.value.current_step / progressInfo.value.total_steps) * 100)
})


const allArticlesSelected = computed(() => {
  if (!processedData.value?.json?.documents?.length) return false
  return selectedArticles.value.length === processedData.value.json.documents.length
})


//Config Methods
const openConfigModal = async () => {
  showConfigModal.value = true
  configStatus.value = null
  await loadAIConfiguration()
}

const closeConfigModal = () => {
  showConfigModal.value = false
  configStatus.value = null
}

const loadAIConfiguration = async () => {
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/ai-configuration?company=${company}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.success && data.configuration) {
        // Mask sensitive data for display
        aiConfig.value = {
          bedrock_api_key: data.configuration.bedrock_api_key ? '••••••••••••••••' : '',
          genesys_client_id: data.configuration.genesys_client_id || '',
          genesys_client_secret: data.configuration.genesys_client_secret ? '••••••••••••••••' : '',
          genesys_environment: data.configuration.genesys_environment || 'usw2.pure.cloud'
        }
        
        // Update Genesys connection status based on whether credentials exist
        const hasGenesysConfig = data.configuration.genesys_client_id && data.configuration.genesys_client_secret
        if (hasGenesysConfig) {
          // Test connection silently
          testGenesysConnection()
        } else {
          genesysStatus.value.connected = false
        }
        
        console.log('AI configuration loaded successfully')
      } else {
        console.log('No AI configuration found, using defaults')
        genesysStatus.value.connected = false
      }
    } else {
      console.warn('Failed to load AI configuration:', response.status)
      genesysStatus.value.connected = false
    }
  } catch (error) {
    console.error('Failed to load AI configuration:', error)
    genesysStatus.value.connected = false
  }
}

const saveAIConfiguration = async () => {
  savingConfig.value = true
  configStatus.value = null
  const company = localStorage.getItem("companyID")
  try {
    // Only send non-masked values
    const configData = {}
    
    if (aiConfig.value.bedrock_api_key && !aiConfig.value.bedrock_api_key.includes('••••')) {
      configData.bedrock_api_key = aiConfig.value.bedrock_api_key
    }
    
    if (aiConfig.value.genesys_client_id) {
      configData.genesys_client_id = aiConfig.value.genesys_client_id
    }
    
    if (aiConfig.value.genesys_client_secret && !aiConfig.value.genesys_client_secret.includes('••••')) {
      configData.genesys_client_secret = aiConfig.value.genesys_client_secret
    }
    
    configData.genesys_environment = aiConfig.value.genesys_environment

    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/ai-configuration?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(configData)
    })
    
    const result = await response.json()
    
    if (result.success) {
      configStatus.value = {
        type: 'success',
        message: 'Configuration saved successfully!'
      }
      showFlashMessage('success', 'AI Configuration saved successfully')
      
      // Refresh knowledge bases list to test connection
      setTimeout(() => {
        loadKnowledgeBases()
      }, 1000)
    } else {
      configStatus.value = {
        type: 'error',
        message: result.message || 'Failed to save configuration'
      }
    }
  } catch (error) {
    console.error('Failed to save AI configuration:', error)
    configStatus.value = {
      type: 'error',
      message: 'Failed to save configuration'
    }
  } finally {
    savingConfig.value = false
  }
}

const testGenesysConnection = async () => {
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/test-ai-configuration?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    const result = await response.json()
    
    if (result.success) {
      // Check if Genesys status indicates connection
      genesysStatus.value.connected = result.genesys_status === 'Connected'
    } else {
      genesysStatus.value.connected = false
    }
  } catch (error) {
    console.error('Silent Genesys connection test failed:', error)
    genesysStatus.value.connected = false
  }
}

const testConfiguration = async () => {
  testingConfig.value = true
  configStatus.value = null
  const company = localStorage.getItem("companyID")
  try {
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/test-ai-configuration?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    
    const result = await response.json()
    
    if (result.success) {
      configStatus.value = {
        type: 'success',
        message: `Connection test successful! Bedrock: ${result.bedrock_status}, Genesys: ${result.genesys_status}`
      }
    } else {
      configStatus.value = {
        type: 'error',
        message: result.message || 'Connection test failed'
      }
    }
  } catch (error) {
    console.error('Configuration test failed:', error)
    configStatus.value = {
      type: 'error',
      message: 'Connection test failed'
    }
  } finally {
    testingConfig.value = false
  }
}

// Approval system methods - NEW
const getApprovalStatus = (index) => {
  return articleApprovalStates.value[index] || 'pending'
}

const getApprovalStatusText = (index) => {
  const status = getApprovalStatus(index)
  switch (status) {
    case 'approved': return 'Approved'
    case 'rejected': return 'Rejected'
    default: return 'Pending'
  }
}

const getArticleRowClass = (index) => {
  const status = getApprovalStatus(index)
  return `approval-${status}`
}

const initializeApprovalStates = () => {
  // Reset approval states when new data is processed
  articleApprovalStates.value = {}
  importedApprovedCount.value = 0
  selectedArticles.value = []
}

// Selection methods - ADD THESE
const toggleArticleSelection = (index) => {
  const selectedIndex = selectedArticles.value.indexOf(index)
  if (selectedIndex > -1) {
    selectedArticles.value.splice(selectedIndex, 1)
  } else {
    selectedArticles.value.push(index)
  }
}

const toggleAllArticles = () => {
  if (allArticlesSelected.value) {
    selectedArticles.value = []
  } else {
    selectedArticles.value = processedData.value.json.documents.map((_, index) => index)
  }
}

const approveSelectedArticles = () => {
  if (selectedArticles.value.length === 0) return
  
  console.log('=== APPROVING ARTICLES ===')
  console.log('Selected articles to approve:', selectedArticles.value)
  console.log('Current approval states before approval:', { ...articleApprovalStates.value })
  
  selectedArticles.value.forEach(index => {
    const article = processedData.value.json.documents[index]
    const title = article.published?.title || article.draft?.title || 'Untitled'
    const status = article.published ? 'Published' : 'Draft'
    
    // Set approval status
    articleApprovalStates.value[index] = 'approved'
    
    console.log(`✅ Approved article ${index}: "${title}" (${status})`)
  })
  
  console.log('Approval states after approval:', { ...articleApprovalStates.value })
  console.log('=== APPROVAL COMPLETE ===')
  
  showFlashMessage('success', `Approved ${selectedArticles.value.length} articles`)
  selectedArticles.value = []
}

const onEditorReady = (quill) => {
  console.log('Editor is ready:', quill)
  editorInstance.value = quill
  
  // If we have pending content, set it now
  if (pendingContent.value) {
    console.log('Setting pending content:', pendingContent.value)
    quill.root.innerHTML = pendingContent.value
    pendingContent.value = ''
  }
}

const rejectSelectedArticles = () => {
  if (selectedArticles.value.length === 0) return
  
  selectedArticles.value.forEach(index => {
    articleApprovalStates.value[index] = 'rejected'
  })
  
  showFlashMessage('info', `Rejected ${selectedArticles.value.length} articles`)
  selectedArticles.value = []
}

const resetSelectedApprovals = () => {
  if (selectedArticles.value.length === 0) return
  
  selectedArticles.value.forEach(index => {
    articleApprovalStates.value[index] = 'pending'
  })
  
  showFlashMessage('info', `Reset ${selectedArticles.value.length} articles to pending`)
  selectedArticles.value = []
}

// Enhanced import methods - NEW
// CORRECTED: importApprovedArticles method with proper Genesys format
const importApprovedArticles = async () => {
  if (!selectedKnowledgeBase.value) {
    showFlashMessage('error', 'No knowledge base selected')
    return
  }

  console.log('=== IMPORT PROCESS START ===')
  console.log('All current approval states:', { ...articleApprovalStates.value })
  console.log('Total documents:', processedData.value?.json?.documents?.length || 0)
  
  if (!processedData.value?.json?.documents) {
    showFlashMessage('error', 'No documents found to import')
    return
  }
  
  // VALIDATION: Ensure all articles have valid IDs before import
  let hasInvalidIds = false
  processedData.value.json.documents.forEach((article, index) => {
    if (!article.id) {
      console.error(`❌ Article ${index} missing ID:`, article)
      hasInvalidIds = true
      // Fix it immediately
      article.id = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
      console.log(`🔧 Generated emergency ID: ${article.id}`)
    }
  })
  
  if (hasInvalidIds) {
    console.log('⚠️ Fixed missing IDs before import')
  }
  
  // Get approved article indices with ID tracking
  const approvedIndices = []
  
  for (let i = 0; i < processedData.value.json.documents.length; i++) {
    const approvalStatus = articleApprovalStates.value[i] || 'pending'
    const article = processedData.value.json.documents[i]
    
    if (!article) {
      console.log(`Article ${i}: NULL/UNDEFINED`)
      continue
    }
    
    const title = article.published?.title || article.draft?.title || 'Untitled'
    const status = article.published ? 'Published' : 'Draft'
    const articleId = article.id || 'NO-ID'
    
    console.log(`Article ${i}: "${title}" (${status}) [ID: ${articleId}] - Approval: ${approvalStatus}`)
    
    if (approvalStatus === 'approved') {
      approvedIndices.push(i)
      console.log(`  → WILL BE IMPORTED [ID: ${articleId}]`)
    } else {
      console.log(`  → SKIPPED (not approved)`)
    }
  }
  
  console.log('Final approved indices for import:', approvedIndices)
  
  if (approvedIndices.length === 0) {
    console.log('❌ No approved articles found')
    showFlashMessage('error', 'No approved articles found to import. Please approve some articles first.')
    return
  }

  importingManually.value = true
  try {
    // Process approved articles with STRICT ID validation
    const approvedArticlesData = approvedIndices.map(index => {
      const originalArticle = processedData.value.json.documents[index]
      const articleTitle = originalArticle.published?.title || originalArticle.draft?.title || 'Untitled'
      let articleId = originalArticle.id
      
      // CRITICAL: Ensure article has a valid ID
      if (!articleId) {
        articleId = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
        console.log(`🚨 EMERGENCY ID GENERATION for "${articleTitle}": ${articleId}`)
      }
      
      // Determine if this should be published
      const shouldBePublished = !!originalArticle.published
      
      console.log(`Processing for import: "${articleTitle}" [ID: ${articleId}]`, {
        index: index,
        hasDraft: !!originalArticle.draft,
        hasPublished: !!originalArticle.published,
        willBePublished: shouldBePublished
      })
      
      if (shouldBePublished) {
        // Create PUBLISHED article - ensure ID
        const publishedArticle = {
          id: articleId,
          published: JSON.parse(JSON.stringify(originalArticle.published))
        }
        
        if (!publishedArticle.published.dateImport) {
          publishedArticle.published.dateImport = Date.now()
        }
        
        console.log(`✅ Created PUBLISHED import structure for "${articleTitle}" [ID: ${articleId}]`)
        return publishedArticle
        
      } else {
        // Create DRAFT article - ensure ID
        const draftArticle = {
          id: articleId,
          draft: JSON.parse(JSON.stringify(originalArticle.draft))
        }
        
        console.log(`📝 Created DRAFT import structure for "${articleTitle}" [ID: ${articleId}]`)
        return draftArticle
      }
    }).filter(article => article !== null)

    console.log(`Created ${approvedArticlesData.length} articles for import`)
    
    // FINAL VALIDATION: Check all import articles have IDs
    const invalidImportArticles = approvedArticlesData.filter(article => !article.id)
    if (invalidImportArticles.length > 0) {
      console.error('❌ CRITICAL: Articles without IDs found in import data:', invalidImportArticles)
      throw new Error(`${invalidImportArticles.length} articles missing IDs in import data`)
    }
    
    // Log the final import data structure
    approvedArticlesData.forEach((article, i) => {
      const title = article.published?.title || article.draft?.title
      const status = article.published ? 'PUBLISHED' : 'DRAFT'
      console.log(`Final Import Article ${i}: "${title}" [ID: ${article.id}] - ${status}`)
    })

    // Create import data
    const importData = {
      ...processedData.value.json,
      documents: approvedArticlesData
    }

    // Count published vs draft
    const publishedCount = approvedArticlesData.filter(a => !!a.published).length
    const draftCount = approvedArticlesData.filter(a => !!a.draft).length

    console.log(`Import summary:`)
    console.log(`- Published articles: ${publishedCount}`)
    console.log(`- Draft articles: ${draftCount}`)
    console.log(`- Total articles: ${approvedArticlesData.length}`)

    // Send to API
    console.log('Sending to API...')
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/upload-to-knowledge-base?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        knowledge_base_id: selectedKnowledgeBase.value.id,
        json_data: importData
      })
    })

    const result = await response.json()
    console.log('API Response:', result)
    
    if (result.success) {
      processedData.value.import_result = result.result
      importedApprovedCount.value = approvedArticlesData.length
      showFlashMessage('success', `Successfully imported ${approvedArticlesData.length} approved articles (${publishedCount} published, ${draftCount} drafts) to ${selectedKnowledgeBase.value.name}`)
      console.log('✅ IMPORT SUCCESSFUL')
    } else {
      throw new Error(result.message || 'Import failed')
    }
  } catch (error) {
    console.error('❌ Import failed:', error)
    processedData.value.import_error = error.message
    showFlashMessage('error', `Import failed: ${error.message}`)
  } finally {
    importingManually.value = false
    console.log('=== IMPORT PROCESS END ===')
  }
}


const handleFileChange = (event) => {
  selectedFiles.value = Array.from(event.target.files)
}

const autoResizeTextarea = (event) => {
  const textarea = event.target
  textarea.style.height = 'auto'
  textarea.style.height = Math.max(120, textarea.scrollHeight) + 'px'
}

const getSourceTypeLabel = () => {
  switch (activeSource.value) {
    case 'url': return 'Website URL'
    case 'file': return 'Document Import'
    case 'text': return 'Direct Text Input'
    default: return 'Unknown'
  }
}

const getContentLength = () => {
  if (activeSource.value === 'text' && textInput.value) {
    return textInput.value.length.toLocaleString()
  }
  return null
}

const formatUrl = (url) => {
  try {
    const urlObj = new URL(url)
    return `${urlObj.hostname}${urlObj.pathname}`
  } catch {
    return url
  }
}

const getCurrentArticleData = (article) => {
  // Return published if it exists, otherwise return draft, otherwise return empty structure
  const data = article.published || article.draft || {
    title: '',
    alternatives: [],
    category: { name: 'General Information' },
    labels: [],
    visible: true,
    variations: [{
      name: "Default",
      priority: 1,
      body: {
        blocks: [{
          type: "Paragraph",
          paragraph: {
            blocks: [{ type: "Text", text: { text: "" } }],
            properties: { fontType: "Paragraph" }
          }
        }]
      }
    }]
  }
  
  return data
}

const getLogEntryClass = (log) => {
  if (log.message.includes('Error') || log.type === 'error') return 'log-error'
  if (log.message.includes('Complete') || log.message.includes('success') || log.message.includes('✅')) return 'log-success'
  if (log.message.includes('Warning') || log.message.includes('⚠️')) return 'log-warning'
  if (log.message.includes('🚀') || log.message.includes('import')) return 'log-import'
  return 'log-info'
}

const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString()
}

const getArticleStatus = (article) => {
  // If article has published property, it's published
  if (article.published) {
    return 'Published'
  }
  // Otherwise it's a draft
  return 'Draft'
}

const getArticleStatusClass = (article) => {
  const status = getArticleStatus(article)
  return status === 'Published' ? 'published' : 'draft'
}

// Genesys Knowledge Base Methods
const loadKnowledgeBases = async () => {
  loadingKnowledgeBases.value = true
  try {
    const company = localStorage.getItem("companyID") || "default"
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases?company=${company}`)
    const data = await response.json()
    
    if (data.success) {
      knowledgeBases.value = data.knowledge_bases.sort((a, b) => a.name.localeCompare(b.name))
      genesysStatus.value.connected = true
      showFlashMessage('success', `Loaded ${data.knowledge_bases.length} knowledge bases`)
    } else {
      throw new Error('Failed to load knowledge bases')
    }
  } catch (error) {
    console.error('Failed to load knowledge bases:', error)
    genesysStatus.value.connected = false
    showFlashMessage('error', `Failed to load knowledge bases: ${error.message}`)
    knowledgeBases.value = []
  } finally {
    loadingKnowledgeBases.value = false
  }
}

const importToKnowledgeBase = async () => {

  const company = localStorage.getItem("companyID") || "default"
  if (!selectedKnowledgeBase.value || !processedData.value?.json) {
    showFlashMessage('error', 'No knowledge base selected or no data to import')
    return
  }

  importingManually.value = true
  try {
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/import-to-knowledge-base?company=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        knowledge_base_id: selectedKnowledgeBase.value.id,
        json_data: processedData.value.json
      })
    })

    const result = await response.json()
    
    if (result.success) {
      processedData.value.import_result = result.result
      showFlashMessage('success', `Successfully imported to ${selectedKnowledgeBase.value.name}`)
    } else {
      throw new Error(result.message || 'Import failed')
    }
  } catch (error) {
    console.error('Manual import failed:', error)
    processedData.value.import_error = error.message
    showFlashMessage('error', `Import failed: ${error.message}`)
  } finally {
    importingManually.value = false
  }
}

// Add these functions to your component
const convertGenesysToHtml = (article) => {
  console.log('Converting article to HTML:', article)
  
  let html = ''
  
  try {
    // Check both published and draft for content
    const articleData = article.published || article.draft || {}
    const blocks = articleData.variations?.[0]?.body?.blocks || []
    
    console.log('Found blocks:', blocks)
    
    if (blocks.length === 0) {
      return '<p>No content available - start editing here...</p>'
    }
    
    for (const block of blocks) {
      if (block.type === 'Paragraph') {
        const fontType = block.paragraph?.properties?.fontType || 'Paragraph'
        let tag = 'p'
        
        if (fontType === 'Heading3') tag = 'h3'
        else if (fontType === 'Heading4') tag = 'h4'
        
        let content = ''
        const textBlocks = block.paragraph?.blocks || []
        
        for (const textBlock of textBlocks) {
          if (textBlock.type === 'Text') {
            let text = textBlock.text?.text || ''

            // Apply formatting
            if (textBlock.text?.marks?.includes('Bold')) {
              text = `<strong>${text}</strong>`
            }

            // Handle hyperlinks
            if (textBlock.text?.hyperlink) {
              text = `<a href="${textBlock.text.hyperlink}">${text}</a>`
            }

            content += text
          }
          else if (textBlock.type === 'Image') {
            // Handle Image blocks within paragraphs
            const imageUrl = textBlock.image?.url || ''
            const altText = textBlock.image?.properties?.altText || 'Image'
            if (imageUrl) {
              content += `<img src="${imageUrl}" alt="${altText}" style="max-width: 100%; height: auto;" />`
            }
          }
        }
        
        // Only add non-empty content
        if (content.trim()) {
          html += `<${tag}>${content}</${tag}>`
        }
      }
      else if (block.type === 'OrderedList' || block.type === 'UnorderedList') {
        const listTag = block.type === 'OrderedList' ? 'ol' : 'ul'
        html += `<${listTag}>`
        
        const listItems = block.list?.blocks || []
        for (const item of listItems) {
          const itemBlocks = item.blocks || []
          let itemContent = ''
          
          for (const textBlock of itemBlocks) {
            if (textBlock.type === 'Text') {
              itemContent += textBlock.text?.text || ''
            }
          }
          
          if (itemContent.trim()) {
            html += `<li>${itemContent}</li>`
          }
        }
        
        html += `</${listTag}>`
      }
    }
    
    console.log('Generated HTML:', html)
    
    // Return default content if nothing was generated
    return html || '<p>Start editing your article content here...</p>'
    
  } catch (error) {
    console.error('Error converting Genesys to HTML:', error)
    return '<p>Error loading content - please refresh and try again.</p>'
  }
}

const convertHtmlToGenesys = (html) => {
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  
  const blocks = []
  
  // Convert each child element to Genesys blocks
  for (const element of tempDiv.children) {
    const tagName = element.tagName.toLowerCase()
    
    if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
      let fontType = 'Paragraph'
      if (tagName === 'h3') fontType = 'Heading3'
      else if (tagName === 'h4') fontType = 'Heading4'
      
      const textBlocks = []
      
      // Handle mixed content (text + formatting)
      for (const node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.trim()) {
            textBlocks.push({
              type: 'Text',
              text: { text: node.textContent }
            })
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.tagName.toLowerCase() === 'img') {
            // Handle image elements
            textBlocks.push({
              type: 'Image',
              image: {
                url: node.src || '',
                properties: {
                  width: 56.25,
                  widthWithUnit: {
                    value: 900.0,
                    unit: 'Px'
                  },
                  altText: node.alt || 'Image'
                }
              }
            })
          } else {
            const text = { text: node.textContent }

            if (node.tagName.toLowerCase() === 'strong' || node.tagName.toLowerCase() === 'b') {
              text.marks = ['Bold']
            }

            if (node.tagName.toLowerCase() === 'a' && node.href) {
              text.hyperlink = node.href
            }

            textBlocks.push({
              type: 'Text',
              text: text
            })
          }
        }
      }
      
      if (textBlocks.length === 0) {
        textBlocks.push({
          type: 'Text',
          text: { text: element.textContent || '' }
        })
      }
      
      blocks.push({
        type: 'Paragraph',
        paragraph: {
          blocks: textBlocks,
          properties: { fontType }
        }
      })
    }
    else if (['ol', 'ul'].includes(tagName)) {
      const listType = tagName === 'ol' ? 'OrderedList' : 'UnorderedList'
      const listBlocks = []
      
      for (const li of element.querySelectorAll('li')) {
        listBlocks.push({
          type: 'ListItem',
          blocks: [{
            type: 'Text',
            text: { text: li.textContent || '' }
          }],
          properties: { fontType: 'Paragraph' }
        })
      }
      
      blocks.push({
        type: listType,
        list: { blocks: listBlocks }
      })
    }
  }
  
  return blocks
}

// Article Management Methods
const getArticlePreview = (article) => {
  try {
    // Check both published and draft for content
    const contentData = article.published || article.draft
    
    if (contentData?.variations?.[0]?.body?.blocks?.length > 0) {
      const firstBlock = contentData.variations[0].body.blocks[0]
      if (firstBlock.type === 'Paragraph' && firstBlock.paragraph?.blocks?.length > 0) {
        const text = firstBlock.paragraph.blocks[0].text?.text || ''
        return text.length > 100 ? text.substring(0, 100) + '...' : text
      }
    }
    return 'No content preview available'
  } catch (error) {
    console.warn('Error getting article preview:', error)
    return 'Preview unavailable'
  }
}

const toggleArticleVisibility = (index, isVisible) => {
  const article = processedData.value.json.documents[index]
  
  if (article.published) {
    article.published.visible = isVisible
  } else if (article.draft) {
    article.draft.visible = isVisible
  }
  
  console.log(`Updated visibility for article ${index}:`, isVisible)
}

const editArticle = async (index) => {
  console.log('=== EDITING ARTICLE ===')
  console.log('Editing article at index:', index)
  console.log('Current approval status:', getApprovalStatus(index))
  
  editingArticleIndex.value = index
  const originalArticle = processedData.value.json.documents[index]
  
  console.log('Original article:', {
    id: originalArticle.id,
    title: originalArticle.published?.title || originalArticle.draft?.title,
    hasDraft: !!originalArticle.draft,
    hasPublished: !!originalArticle.published
  })
  
  // Create editing copy - PRESERVE THE ORIGINAL ID
  editingArticle.value = JSON.parse(JSON.stringify(originalArticle))
  
  // Determine current status based on which key exists
  editingArticleStatus.value = originalArticle.published ? 'Published' : 'Draft'
  
  // Get the content from whichever key exists
  const contentData = originalArticle.published || originalArticle.draft
  
  if (contentData?.labels?.length > 0) {
    labelsText.value = contentData.labels.map(l => l.name).join(', ')
  } else {
    labelsText.value = ''
  }
  
  // Convert content to HTML
  const convertedHtml = convertGenesysToHtml(originalArticle)
  console.log('Editing article:', {
    id: originalArticle.id,
    title: contentData?.title,
    status: editingArticleStatus.value,
    hasDraft: !!originalArticle.draft,
    hasPublished: !!originalArticle.published,
    approvalStatus: getApprovalStatus(index)
  })
  
  showArticleEditor.value = true
  
  await nextTick()
  
  // Set content in editor
  setTimeout(() => {
    if (editorInstance.value) {
      console.log('Setting content on ready editor')
      editorInstance.value.root.innerHTML = convertedHtml
    } else {
      console.log('Editor not ready, storing content for later')
      pendingContent.value = convertedHtml
    }
    
    wysiwygContent.value = convertedHtml
  }, 150)
}

const updateLabels = () => {
  const labels = labelsText.value.split(',').map(label => label.trim()).filter(label => label.length > 0)
  editingArticle.value.draft.labels = labels.map(name => ({ name }))
}

const addAlternative = () => {
  if (!editingArticle.value.draft.alternatives) {
    editingArticle.value.draft.alternatives = []
  }
  editingArticle.value.draft.alternatives.push({ phrase: '', autocomplete: true })
}

const removeAlternative = (index) => {
  editingArticle.value.draft.alternatives.splice(index, 1)
}



const closeArticleEditor = () => {
  showArticleEditor.value = false
  editingArticle.value = null
  editingArticleIndex.value = -1
}

const updateArticleStatus = () => {
  if (editingArticleStatus.value === 'Draft') {
    // Keep only draft, remove published
    if (!editingArticle.value.draft) {
      editingArticle.value.draft = editingArticle.value.published || {
        title: '',
        alternatives: [],
        category: { name: 'General Information' },
        labels: [],
        visible: true,
        variations: [{
          name: "Default",
          priority: 1,
          body: {
            blocks: [{
              type: "Paragraph",
              paragraph: {
                blocks: [{ type: "Text", text: { text: "" } }],
                properties: { fontType: "Paragraph" }
              }
            }]
          }
        }]
      }
    }
    // Remove published completely for draft status
    delete editingArticle.value.published
    
  } else if (editingArticleStatus.value === 'Published') {
    // Ensure we have both draft and published for published status
    if (!editingArticle.value.draft) {
      editingArticle.value.draft = {
        title: '',
        alternatives: [],
        category: { name: 'General Information' },
        labels: [],
        visible: true,
        variations: [{
          name: "Default",
          priority: 1,
          body: {
            blocks: [{
              type: "Paragraph",
              paragraph: {
                blocks: [{ type: "Text", text: { text: "" } }],
                properties: { fontType: "Paragraph" }
              }
            }]
          }
        }]
      }
    }
    
    // Create published from current draft content
    editingArticle.value.published = JSON.parse(JSON.stringify(editingArticle.value.draft))
  }
}

const saveArticleChanges = () => {
  if (editingArticleIndex.value >= 0 && editingArticle.value) {
    console.log('=== SAVING ARTICLE ===')
    console.log('Editing article index:', editingArticleIndex.value)
    
    // Get the ORIGINAL article to preserve its ID and approval status
    const originalArticle = processedData.value.json.documents[editingArticleIndex.value]
    const originalId = originalArticle.id
    const currentApprovalStatus = articleApprovalStates.value[editingArticleIndex.value] || 'pending'
    
    console.log('Original article ID:', originalId)
    console.log('Current approval status BEFORE save:', currentApprovalStatus)
    
    // Convert WYSIWYG HTML back to Genesys blocks
    const blocks = convertHtmlToGenesys(wysiwygContent.value)
    
    // Prepare the content structure
    const contentStructure = {
      title: editingArticle.value.draft?.title || editingArticle.value.published?.title || '',
      alternatives: editingArticle.value.draft?.alternatives || editingArticle.value.published?.alternatives || [],
      category: editingArticle.value.draft?.category || editingArticle.value.published?.category || { name: 'General Information' },
      labels: editingArticle.value.draft?.labels || editingArticle.value.published?.labels || [],
      visible: editingArticle.value.draft?.visible !== undefined ? editingArticle.value.draft.visible : 
               editingArticle.value.published?.visible !== undefined ? editingArticle.value.published.visible : true,
      variations: [{
        name: "Default",
        priority: 1,
        body: { blocks: blocks }
      }]
    }
    
    console.log('Saving article with status:', editingArticleStatus.value)
    
    // Create the article structure based on the selected status
    // CRITICAL: Use the ORIGINAL ID, not a new one
    const savedArticle = {
      id: originalId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }
    
    if (editingArticleStatus.value === 'Published') {
      // Published article - ONLY published key
      savedArticle.published = {
        ...contentStructure,
        dateImport: Date.now()
      }
      console.log('✅ Article saved as Published - created published key only')
    } else {
      // Draft article - ONLY draft key  
      savedArticle.draft = contentStructure
      console.log('📝 Article saved as Draft - created draft key only')
    }
    
    console.log('Preserving original ID:', savedArticle.id)
    console.log('Preserving approval status:', currentApprovalStatus)
    
    // Save back to main data AT THE SAME INDEX
    processedData.value.json.documents[editingArticleIndex.value] = savedArticle
    
    // IMMEDIATELY restore the approval status using the SAME index
    articleApprovalStates.value[editingArticleIndex.value] = currentApprovalStatus
    
    console.log('Article saved. Final structure:', {
      index: editingArticleIndex.value,
      id: savedArticle.id,
      title: savedArticle.published?.title || savedArticle.draft?.title,
      hasDraft: !!savedArticle.draft,
      hasPublished: !!savedArticle.published,
      finalStatus: savedArticle.published ? 'Published' : 'Draft'
    })
    console.log('Approval status AFTER save:', articleApprovalStates.value[editingArticleIndex.value])
    console.log('=== SAVE COMPLETE ===')
    
    const finalStatus = savedArticle.published ? 'Published' : 'Draft'
    showFlashMessage('success', `Article updated successfully as ${finalStatus} (approval: ${currentApprovalStatus})`)
    closeArticleEditor()
  }
}

const deleteArticle = (index) => {
  if (processedData.value.json.documents.length > 1) {
    // Remove from selected articles if present
    const selectedIndex = selectedArticles.value.indexOf(index)
    if (selectedIndex > -1) {
      selectedArticles.value.splice(selectedIndex, 1)
    }
    
    // Adjust selected articles indices for articles after the deleted one
    selectedArticles.value = selectedArticles.value.map(selectedIdx => 
      selectedIdx > index ? selectedIdx - 1 : selectedIdx
    )
    
    // Remove approval state for this article and adjust indices
    delete articleApprovalStates.value[index]
    
    // Shift approval states for articles after the deleted one
    const newApprovalStates = {}
    Object.keys(articleApprovalStates.value).forEach(key => {
      const articleIndex = parseInt(key)
      if (articleIndex < index) {
        newApprovalStates[articleIndex] = articleApprovalStates.value[key]
      } else if (articleIndex > index) {
        newApprovalStates[articleIndex - 1] = articleApprovalStates.value[key]
      }
    })
    articleApprovalStates.value = newApprovalStates
    
    processedData.value.json.documents.splice(index, 1)
    showFlashMessage('success', 'Article deleted successfully')
  } else {
    showFlashMessage('error', 'Cannot delete the last remaining article')
  }
}

const processContent = async () => {
  if (!canProcess.value) return
  
  isProcessing.value = true
  processedData.value = null
  successMessage.value = ''
  errorMessage.value = ''
  currentStatus.value = 'Initializing...'
  progressInfo.value = { current_step: null, total_steps: null }
  progressLog.value = []
  const company = localStorage.getItem("companyID") || "default"
  // Reset approval states when processing new content
  initializeApprovalStates()
  
  try {
    let streamUrl
    let requestBody
    
    switch (activeSource.value) {
      case 'url': {
        // ADD COMPANY PARAMETER TO URL
        streamUrl = process.env.VUE_APP_AI_BASE_URL + `/extract-and-process-stream?company=${encodeURIComponent(company)}`
        requestBody = JSON.stringify({
          url: urlInput.value.trim(),
          link_limit: linkLimit.value,
          auto_upload: false
        })
        break
      }
      case 'file': {
        // ADD COMPANY PARAMETER TO URL
        streamUrl = process.env.VUE_APP_AI_BASE_URL + `/process-files-stream?company=${encodeURIComponent(company)}`
        const formData = new FormData()
        selectedFiles.value.forEach(file => {
          formData.append('files', file)
        })
        requestBody = formData
        break
      }
      case 'text': {
        // ADD COMPANY PARAMETER TO URL
        streamUrl = process.env.VUE_APP_AI_BASE_URL + `/process-text-stream?company=${encodeURIComponent(company)}`
        requestBody = JSON.stringify({
          content: textInput.value.trim(),
          source_info: 'Manual input',
          auto_upload: false
        })
        break
      }
    }
    
    await startStreaming(streamUrl, requestBody)
    
    // CRITICAL FIX: Ensure all articles have valid IDs after processing
    if (processedData.value?.json?.documents) {
      console.log('=== ENSURING VALID IDs ===')
      processedData.value.json.documents.forEach((article, index) => {
        if (!article.id) {
          const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          article.id = newId
          console.log(`Generated ID for article ${index}: ${newId} - "${article.draft?.title || article.published?.title}"`)
        } else {
          console.log(`Article ${index} already has ID: ${article.id} - "${article.draft?.title || article.published?.title}"`)
        }
      })
      console.log('=== ID CHECK COMPLETE ===')
    }
    
  } catch (error) {
    const errorMsg = error?.message || 'Processing failed'
    errorMessage.value = `Error: ${errorMsg}`
    currentStatus.value = `Error: ${errorMsg}`
    addProgressLog(`Error: ${errorMsg}`, 'error')
    showFlashMessage('error', `Error: ${errorMsg}`)
    console.error('Processing error:', error)
  } finally {
    isProcessing.value = false
  }
}

const addProgressLog = (message, type = 'info') => {
  progressLog.value.push({
    message,
    type,
    timestamp: new Date().toISOString()
  })
  
  // Keep only last 20 log entries
  if (progressLog.value.length > 20) {
    progressLog.value = progressLog.value.slice(-20)
  }
}

const startStreaming = async (url, body) => {
  return new Promise((resolve, reject) => {
    const requestOptions = {
      method: 'POST',
      headers: activeSource.value === 'file' ? {} : { 'Content-Type': 'application/json' },
      body: body
    }
    
    fetch(url, requestOptions)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        
        const readStream = () => {
          return reader.read().then(({ done, value }) => {
            if (done) {
              resolve()
              return
            }
            
            const chunk = decoder.decode(value)
            buffer += chunk
            
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6))
                  handleStreamUpdate(data)
                } catch (e) {
                  console.warn('Parse error:', e.message)
                }
              }
            }
            
            return readStream()
          })
        }
        
        return readStream()
      })
      .catch(error => {
        reject(error)
      })
  })
}

const handleStreamUpdate = (data) => {
  if (data.type === 'heartbeat') {
    return
  }
  
  currentStatus.value = data.message
  addProgressLog(data.message, data.type)
  
  if (data.current_step !== undefined) {
    progressInfo.value.current_step = data.current_step
  }
  
  if (data.total_steps !== undefined) {
    progressInfo.value.total_steps = data.total_steps
  }
  
  // Handle chunked data
  if (data.type === 'complete_start') {
    chunkBuffer.value = {
      raw_content: null,
      analysis: null,
      documents: [],
      categories: [],
      labels: [],
      contexts: [],
      version: 2,
      isChunked: true
    }
    return
  }
  
  if (data.type === 'chunk_raw_content' && data.data?.raw_content) {
    chunkBuffer.value.raw_content = data.data.raw_content
    return
  }
  
  if (data.type === 'chunk_analysis' && data.data?.analysis) {
    chunkBuffer.value.analysis = data.data.analysis
    return
  }
  
  if (data.type === 'chunk_documents' && data.data?.json_chunk?.documents) {
    const chunkDocs = data.data.json_chunk.documents
    chunkBuffer.value.documents.push(...chunkDocs)
    
    const chunkInfo = data.data.json_chunk.chunk_info
    if (chunkInfo) {
      currentStatus.value = `Received ${chunkBuffer.value.documents.length} of ${chunkInfo.total_documents} articles...`
    }
    return
  }
  
  if (data.type === 'chunk_metadata' && data.data?.json_metadata) {
    const metadata = data.data.json_metadata
    chunkBuffer.value.categories = metadata.categories || []
    chunkBuffer.value.labels = metadata.labels || []
    chunkBuffer.value.contexts = metadata.contexts || []
    chunkBuffer.value.version = metadata.version || 2
    return
  }
  
  if (data.type === 'chunk_json' && data.data?.json) {
    const json = data.data.json
    chunkBuffer.value.documents = json.documents || []
    chunkBuffer.value.categories = json.categories || []
    chunkBuffer.value.labels = json.labels || []
    chunkBuffer.value.contexts = json.contexts || []
    chunkBuffer.value.version = json.version || 2
    return
  }
  
  if (data.type === 'complete_final' && chunkBuffer.value.isChunked) {
    // Assemble the final result from chunks
    const assembledData = {
      success: true,
      json: {
        version: chunkBuffer.value.version,
        documents: chunkBuffer.value.documents,
        categories: chunkBuffer.value.categories,
        labels: chunkBuffer.value.labels,
        contexts: chunkBuffer.value.contexts
      },
      analysis: chunkBuffer.value.analysis || 'Processing completed successfully',
      raw_content: chunkBuffer.value.raw_content || {}
    }
    
    // CRITICAL: Ensure all articles have IDs
    if (assembledData.json.documents) {
      assembledData.json.documents.forEach((article, index) => {
        if (!article.id) {
          article.id = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
          console.log(`Generated ID for chunked article ${index}: ${article.id}`)
        }
      })
    }
    
    processedData.value = assembledData
    
    // Initialize approval states for new articles
    initializeApprovalStates()
    
    let successMsg = `Content processed successfully! Generated ${chunkBuffer.value.documents.length} articles.`
    successMessage.value = successMsg
    showFlashMessage('success', successMsg)
    
    // Reset chunk buffer
    chunkBuffer.value = {
      raw_content: null,
      analysis: null,
      documents: [],
      categories: [],
      labels: [],
      contexts: [],
      version: 2,
      isChunked: false
    }
    return
  }
  
  // Handle normal completion (non-chunked)
  if (data.type === 'complete' && data.data && !chunkBuffer.value.isChunked) {
    // CRITICAL: Ensure all articles have IDs
    if (data.data.json?.documents) {
      data.data.json.documents.forEach((article, index) => {
        if (!article.id) {
          article.id = `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`
          console.log(`Generated ID for article ${index}: ${article.id}`)
        }
      })
    }
    
    processedData.value = data.data
    
    // Initialize approval states for new articles
    initializeApprovalStates()
    
    let successMsg = 'Content processed successfully!'
    successMessage.value = successMsg
    showFlashMessage('success', successMsg)
  } else if (data.type === 'error') {
    errorMessage.value = data.message
    showFlashMessage('error', data.message)
  }
}

// const downloadJson = () => {
//   if (processedData.value?.json) {
//     downloadFile(
//       JSON.stringify(processedData.value.json, null, 2),
//       'genesys-kb-article.json',
//       'application/json'
//     )
//   }
// }

// const downloadFile = (content, filename, mimeType) => {
//   const blob = new Blob([content], { type: mimeType })
//   const url = URL.createObjectURL(blob)
//   const link = document.createElement('a')
//   link.href = url
//   link.download = filename
//   document.body.appendChild(link)
//   link.click()
//   document.body.removeChild(link)
//   URL.revokeObjectURL(url)
  
//   showFlashMessage('success', `${filename} downloaded successfully`)
// }

const resetForm = () => {
  urlInput.value = ''
  textInput.value = ''
  selectedFiles.value = []
  activeSource.value = 'url'
  processedData.value = null
  successMessage.value = ''
  errorMessage.value = ''
  linkLimit.value = 0
  includeAnalysis.value = true
  optimizeTokens.value = true
  structuredOutput.value = true
  currentStatus.value = ''
  progressInfo.value = { current_step: null, total_steps: null }
  progressLog.value = []
  selectedKnowledgeBase.value = null
  
  // Reset approval states
  initializeApprovalStates()
  
  try {
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  } catch (error) {
    console.warn('File input reset error:', error)
  }
  
  showFlashMessage('success', 'Form reset successfully')
}

const showFlashMessage = (type, message) => {
  const color = type === 'success' ? "#059669" : type === 'info' ? "#3b82f6" : "#dc2626"
  const textColor = "#ffffff"
  
  try {
    if (typeof flashMessage === 'function') {
      flashMessage(undefined, color, textColor, message)
    }
  } catch (error) {
    console.warn('FlashMessage error:', error)
  }
  
  if (type === 'success') {
    successMessage.value = message
    setTimeout(() => successMessage.value = '', 5000)
  } else if (type === 'error') {
    errorMessage.value = message
    setTimeout(() => errorMessage.value = '', 8000)
  }
}

onMounted( async () => {
  await loadAIConfiguration()
  // Load knowledge bases on component mount
  loadKnowledgeBases()
})
</script>

<style scoped>
/* Approval System Styles */
.approved-count {
  color: #059669;
  font-weight: 600;
}

.approval-summary-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}

.approval-summary-section h5 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
}

.approval-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.approval-stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
}

.approval-stat-item.approved {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.approval-stat-item.pending {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.approval-stat-item.rejected {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.stat-icon {
  font-size: 16px;
}

.stat-text {
  font-size: 13px;
}

.bulk-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-approve-all {
  background-color: #059669;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-approve-all:hover:not(:disabled) {
  background-color: #047857;
}

.btn-reject-all {
  background-color: #dc2626;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-reject-all:hover:not(:disabled) {
  background-color: #b91c1c;
}

.btn-reset-all {
  background-color: #6b7280;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-reset-all:hover:not(:disabled) {
  background-color: #4b5563;
}

.approved-articles-info {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.approved-count-display {
  color: #166534;
  font-weight: 500;
  font-size: 14px;
}

.no-approved-warning {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 12px;
  margin: 16px 0;
}

.warning-message {
  color: #92400e;
  font-weight: 500;
  font-size: 14px;
}

.btn-approved-download {
  background-color: #059669;
  color: white;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
}

.btn-approved-download:hover {
  background-color: #047857;
}

/* Articles Table with Approval */
.articles-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.articles-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.approval-summary-mini {
  display: flex;
  gap: 12px;
  align-items: center;
}

.mini-stat {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
}

.mini-stat.approved {
  background: #dcfce7;
  color: #166534;
}

.mini-stat.pending {
  background: #fef3c7;
  color: #92400e;
}

.mini-stat.rejected {
  background: #fef2f2;
  color: #dc2626;
}

.approval-column {
  width: 100px; /* Changed from 140px to 100px */
}

.approval-cell {
  text-align: center;
  vertical-align: middle;
}

.approval-controls {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 8px;
}

.approval-btn {
  background: none;
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 4px 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  opacity: 0.6;
}

.approval-btn:hover:not(:disabled) {
  opacity: 1;
  transform: scale(1.1);
}

.approval-btn:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.approve-btn.active {
  background: #dcfce7;
  border-color: #059669;
  opacity: 1;
}

.reject-btn.active {
  background: #fef2f2;
  border-color: #dc2626;
  opacity: 1;
}

.reset-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #6b7280;
}

.approval-status {
  margin-top: 4px;
}

.approval-status-badge {
  font-size: 12px; /* Changed from 10px to 12px */
  padding: 4px 10px; /* Changed from 2px 6px to 4px 10px */
  border-radius: 4px; /* Changed from 3px to 4px */
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
}

.approval-status-badge.approved {
  background: #dcfce7;
  color: #166534;
}

.approval-status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.approval-status-badge.rejected {
  background: #fef2f2;
  color: #dc2626;
}

/* Article Row Styling Based on Approval */
.article-row.approval-approved {
  background: #f0fdf4;
  border-left: 4px solid #059669;
}

.article-row.approval-rejected {
  background: #fef2f2;
  border-left: 4px solid #dc2626;
  opacity: 0.7;
}

.article-row.approval-pending {
  background: #fffbeb;
  border-left: 4px solid #f59e0b;
}

/* Enhanced Table Layout */
.title-column {
  width: 25%;
}

.category-column {
  width: 12%;
}

.status-column {
  width: 10%;
}

.visible-column {
  width: 10%;
}

.labels-column {
  width: 15%;
}

.actions-column {
  width: 8%;
}

/* Rest of your existing styles from the original paste.txt file */
.flex-child {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 88vh;
  padding: 0;
  overflow-x: hidden !important;
}

.body-container {
  align-self: unset;
  height: auto;
  width: auto;
  min-height: calc(100vh - 16vh);
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}


.config-btn {
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.config-btn:hover {
  background: #4b5563;
  transform: scale(1.05);
}

/* AI Configuration Modal */
.dialog-ai-config {
  width: 600px;
  max-width: 90vw; /* Responsive width */
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 0;
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%); /* This centers it perfectly */
  background-color: #fff;
  z-index: 100;
  box-shadow: 2px 4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  border: 2px solid #767676;
  margin: 0; /* Remove any margin */
}

.dialog-ai-config .dialog-grid-container {
  display: grid;
  align-items: baseline;
  column-gap: 16px;
  row-gap: 8px;
  grid-template-columns: 140px 1fr;
  margin-bottom: 20px;
}

.dialog-ai-config .dialog-grid-container label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-align: right;
  margin-bottom: 0;
}

.config-status {
  margin: 16px 0;
}

.status-message {
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
}

.status-message.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.status-message.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

/* Common dialog styles (if not already present) */
.dialog-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: -10;
}

.dialog-body {
  z-index: 10;
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  position: relative;
}

.dialog-header {
  margin: 0;
  margin-bottom: 20px;
  font-size: 24px;
  color: #111827;
  font-weight: 600;
}

.close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.close:hover {
  color: #374151;
}

.button-group {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.button-group button {
  min-width: 120px;
}

header h2 {
  color: #111827;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.genesys-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-indicator.connected {
  background: #dcfce7;
  color: #166534;
}

.status-indicator.disconnected {
  background: #fef2f2;
  color: #dc2626;
}

/* FIXED ARTICLE EDITOR LAYOUT STYLES */
#centerdiv.article-editor-mode {
  display: flex;
  flex-wrap: nowrap; /* Prevent wrapping to next line */
  gap: 16px;
  align-items: flex-start; /* Align to top */
}

/* Left column in editor mode - basic article info */
.containerdiv.editor-left-column {
  width: 600px; /* Fixed width instead of percentage */
  min-width: 600px;
  flex-shrink: 0; /* Prevent shrinking */
}

/* Right column in editor mode - content editor */
.containerdiv.editor-content-column {
  flex: 1; /* Take remaining space */
  width: auto;
  max-width: none;
}

/* Ensure content editor takes full available width */
.content-editor {
  width: 100%;
}

/* Mobile responsiveness for editor mode */
@media only screen and (max-width: 1200px) {
  #centerdiv.article-editor-mode {
    flex-direction: column; /* Stack vertically on smaller screens */
  }
  
  .containerdiv.editor-left-column {
    width: 100%;
    min-width: auto;
  }
  
  .containerdiv.editor-content-column {
    width: 100%;
  }
}

/* Enhanced Article Editor Styles for Proper Layout */
.blocks-editor-expanded {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
}

.block-editor-expanded {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
}

.block-header-expanded {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.block-type-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.block-type-section label {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.paragraph-editor-expanded, .list-editor-expanded {
  background: white;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.paragraph-options-expanded {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f3f4f6;
}

.paragraph-options-expanded label {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.text-blocks-expanded {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.text-block-expanded {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
}

.text-input-row {
  margin-bottom: 12px;
}

.text-input-expanded {
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  line-height: 1.5;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.text-input-expanded:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.text-options-expanded {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hyperlink-input-expanded {
  flex: 1;
  min-width: 200px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
}

.list-items-expanded {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.list-item-editor-expanded {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.list-item-content-expanded {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.list-item-number {
  color: #6b7280;
  font-weight: 500;
  min-width: 20px;
  font-size: 14px;
  margin-top: 8px;
}

.list-item-input-expanded {
  flex: 1;
  min-height: 60px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  background: white;
}

.add-block-section-expanded {
  text-align: center;
  padding: 24px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #fafafa;
}

/* Article Editor Inline Styles */
.article-editor, .alternatives-editor-section, .content-preview-section, .content-editor, .article-info-section {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.edit-field {
  margin-bottom: 16px;
}

.edit-field label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.status-options {
  display: flex;
  gap: 16px;
  margin-top: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.radio-option input[type="radio"] {
  margin: 0;
}

.alternatives-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alternative-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.editor-actions {
  margin-top: 20px;
  display: flex;
  gap: 8px;
}

.btn-danger-small {
  background-color: #ef4444;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  min-width: auto;
  border-radius: 12px;
}

.btn-danger-small:hover {
  background-color: #dc2626;
}

.btn-secondary-small {
  background-color: #6c757d;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  min-width: auto;
  border-radius: 12px;
}

.current-labels-display {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  min-height: 32px;
  align-items: center;
}

.current-label-tag {
  background: #e5e7eb;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.no-labels {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
}

/* Rest of existing styles continue with your original CSS... */
/* Replace your existing #subheader styles with this */
.subheader-aligned {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin: 16px 8px;
  align-items: center;
}

.subheader-aligned .info-item {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.subheader-aligned .info-label {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.subheader-aligned .info-value {
  font-weight: 600;
  color: #059669;
  font-size: 14px;
}

.subheader-aligned .info-value.approved-count {
  color: #059669;
}

.subheader-aligned .info-value.import-success {
  color: #166534;
}

/* Responsive behavior */
@media (max-width: 768px) {
  .subheader-aligned {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.member-info {
  display: flex;
}

#centerdiv {
  padding: 0 8px;
  margin-top: 25px;
  display: flex;
  flex-wrap: wrap;
  height: auto;
  width: 98%;
  justify-content: space-between;
  gap: 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 24px 16px;
  margin: 25px 8px 20px 8px;
}

.containerdiv {
  display: flex;
  flex-flow: column;
  height: fit-content;
  width: calc(33.333% - 16px);
  padding: 8px;
  box-sizing: border-box;
  background: transparent;
}

.containerdiv > * {
  padding: 8px;
}

.member-info-content {
  padding: 8px;
}

.processing-section {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.analysis-section-container {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.source-section, .options-section {
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}

.import-success {
  color: #166534;
  font-weight: 600;
}

.import-status-section {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
}

.import-success-message {
  color: #166534;
  font-weight: 500;
  font-size: 14px;
}

.import-success-message small {
  color: #16a34a;
  font-weight: 400;
  display: block;
  margin-top: 4px;
}

.import-error-section {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px;
  margin: 12px 0;
}

.import-error-message {
  color: #dc2626;
  font-weight: 500;
  font-size: 14px;
}

.import-info-section {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
}

.import-info-section h4 {
  margin: 0 0 12px 0;
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
}

.import-details {
  padding: 12px;
  border-radius: 6px;
}

.import-details.success {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
}

.import-details.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.import-status {
  font-weight: 600;
  margin-bottom: 8px;
}

.import-details.success .import-status {
  color: #166534;
}

.import-details.error .import-status {
  color: #dc2626;
}

.import-detail-item {
  margin: 4px 0;
  font-size: 13px;
}

.import-details.success .import-detail-item {
  color: #16a34a;
}

.import-details.error .import-detail-item {
  color: #dc2626;
}

.source-content {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.source-options-row {
  display: flex;
  gap: 8px;
  align-items: center;
  width: fit-content; /* This prevents it from stretching full width */
}

.source-options-row label {
  margin: 0; /* Remove default label margin */
  white-space: nowrap; /* Prevent label text from wrapping */
  font-size: 14px;
  color: #374151;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  margin-bottom: 8px;
}

.radiolist {
  display: flex;
}

.radiolist div {
  padding-right: 1rem;
  padding-bottom: .5rem;
}

.radiolist div input[type="radio"] {
  margin: 0 !important;
  margin-right: 6px !important;
  padding: 0 !important;
  width: auto;
}

.form-control {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.form-control-textarea {
  display: block;
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: inherit;
  resize: vertical;
  font-size: 14px;
  line-height: 1.4;
}

.file-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  width: 97%;
  font-size: 14px;
}

.field-help {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

h4 {
  margin: 10px 0;
  color: #111827;
  font-size: 18px;
  font-weight: 600;
}

h5 {
  margin: 8px 0;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
}

label {
  font-size: 1rem;
  margin: 0;
  font-weight: 500;
  color: #374151;
}

.btn {
  box-shadow: 2px 4px #ccc;
  margin: 0 5px;
  padding: 7px 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  min-width: 70px;
  transition: all 0.2s ease;
}

.btn-rounded {
  border-radius: 20px;
}

.btn-submit {
  background-color: #007bff;
  color: white;
  border-radius: 20px;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border-radius: 20px;
}

.btn-import {
  background-color: #1e40af;
  color: white;
  border-radius: 20px;
}

.btn-import:hover {
  background-color: #1e3a8a;
}

.btn-import:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.btn-icon {
  background: #3b82f6;
  color: white;
  padding: 8px 10px;
  font-size: 14px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: #2563eb;
}

.progress-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.progress-bar-container {
  margin: 16px 0;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  font-weight: 500;
}

.current-status {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin: 16px 0;
}

.status-message {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  flex: 1;
}

.progress-log {
  margin-top: 20px;
}

.log-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fafafa;
}

.log-entry {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  line-height: 1.4;
}

.log-entry:last-child {
  border-bottom: none;
}

.log-timestamp {
  color: #6b7280;
  font-size: 11px;
  min-width: 60px;
  font-family: monospace;
}

.log-message {
  flex: 1;
  color: #374151;
}

.log-entry.log-success {
  background: #f0fdf4;
  border-left: 3px solid #22c55e;
}

.log-entry.log-error {
  background: #fef2f2;
  border-left: 3px solid #ef4444;
}

.log-entry.log-warning {
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
}

.log-entry.log-import {
  background: #f0f9ff;
  border-left: 3px solid #1e40af;
}

.log-entry.log-import .log-message {
  color: #1e40af;
}

.log-entry.log-info {
  background: #f8fafc;
  border-left: 3px solid #3b82f6;
}

.results-summary {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.status-message.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
}

.status-message.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
}

.analysis-section {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
}

.analysis-section h4 {
  margin: 0 0 12px 0;
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
}

.analysis-content {
  color: #1e40af;
  line-height: 1.5;
  font-size: 14px;
}

.urls-section {
  background: #fefce8;
  border: 1px solid #fde047;
  border-radius: 8px;
  padding: 16px;
}

.urls-section h4 {
  margin: 0 0 12px 0;
  color: #a16207;
  font-size: 16px;
  font-weight: 600;
}

.urls-list-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #fde047;
  border-radius: 4px;
  background: white;
}

.urls-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
}

.url-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  background: #fefce8;
  border-radius: 6px;
  border: 1px solid #fbbf24;
}

.url-number {
  font-weight: 600;
  color: white;
  min-width: 20px;
  text-align: center;
  background: #fbbf24;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.url-link {
  color: #1d4ed8;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
  line-height: 1.4;
}

.url-link:hover {
  text-decoration: underline;
}

.getting-started {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.step-number {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-title {
  font-weight: 500;
  color: #111827;
  margin-bottom: 4px;
  font-size: 14px;
}

.step-description {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.4;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.feature-text {
  color: #374151;
  font-weight: 400;
  font-size: 14px;
}

.kb-stats-section h5 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
}

.kb-stats {
  display: flex;
  gap: 16px;
  justify-content: space-around;
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #1e40af;
  line-height: 1;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.action-icon-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0 2px;
}

.action-icon-btn:hover {
  background: #2563eb;
  transform: scale(1.05);
}

.action-icon-btn.delete-btn {
  background: #ef4444;
}

.action-icon-btn.delete-btn:hover {
  background: #dc2626;
}

.action-buttons {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.articles-full-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin: 20px 8px;
  padding: 20px;
}

.articles-table-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  margin-bottom: 16px;
}

.articles-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.articles-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.articles-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-right: 1px solid #e5e7eb;
}

.articles-table th:last-child {
  border-right: none;
}

.articles-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.articles-table tbody tr:hover {
  background: #f9fafb;
}

.articles-table tbody tr:last-child {
  border-bottom: none;
}

.articles-table td {
  padding: 12px 16px;
  vertical-align: top;
  border-right: 1px solid #f3f4f6;
}

.articles-table td:last-child {
  border-right: none;
}

.article-title {
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  line-height: 1.4;
}

.article-preview {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
  max-height: 36px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.rectangle-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.category-badge {
  background: #3b82f6;
  color: white;
}

.status-badge.published {
  background: #dcfce7;
  color: #166534;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.visibility-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
}

.visibility-checkbox {
  width: 16px;
  height: 16px;
}

.visibility-label {
  font-size: 12px;
  color: #6b7280;
}

.labels-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.label-tag {
  background: #f3f4f6;
  color: #374151;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.kb-import-section {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
}

.kb-import-section h5 {
  margin: 0 0 12px 0;
  color: #1e40af;
  font-size: 16px;
  font-weight: 600;
}

.kb-selection {
  margin-bottom: 12px;
}

.kb-dropdown {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  width: 100%;
}

.kb-dropdown:disabled {
  background: #f9fafb;
  color: #6b7280;
}

.kb-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.download-section {
  text-align: center;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

/* New Approval Actions Bar */
.approval-actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
}

.selection-info {
  color: #475569;
  font-weight: 500;
  font-size: 14px;
}

.approval-actions {
  display: flex;
  gap: 8px;
}

.btn-approve {
  background-color: #059669;
  color: white;
  padding: 7px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  transition: all 0.2s ease;
}

.btn-reject {
  background-color: #dc2626;
  color: white;
  padding: 7px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  transition: all 0.2s ease;
}

.btn-reset {
  background-color: #6b7280;
  color: white;
  padding: 7px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  transition: all 0.2s ease;
}

/* Checkbox Column Styles */
.checkbox-column {
  width: 40px;
  text-align: center;
}

.select-all-checkbox, .article-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  align-self: center;
}

.checkbox-cell {
  text-align: center;
  vertical-align: middle;
}

.approval-status-cell {
  text-align: left;
  vertical-align: middle;
}

/* Center checkbox in header */
.checkbox-header-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

/* Disabled edit button */
.action-icon-btn.disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.5;
}

.action-icon-btn.disabled:hover {
  background: #9ca3af;
  transform: none;
}



/* WYSIWYG Editor Styles */
.wysiwyg-editor-container {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
}

.wysiwyg-editor {
  min-height: 400px;
}

.wysiwyg-editor :deep(.ql-editor) {
  min-height: 350px;
  font-size: 14px;
  line-height: 1.6;
  padding: 20px;
}

.wysiwyg-editor :deep(.ql-toolbar) {
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.wysiwyg-editor :deep(.ql-container) {
  border: none;
}

.wysiwyg-editor :deep(.ql-editor.ql-blank::before) {
  color: #9ca3af;
  font-style: italic;
}

/* Mobile responsiveness for approval system */
@media only screen and (max-width: 800px) {
  .approval-controls {
    flex-direction: column;
    gap: 2px;
  }
  
  .approval-btn {
    font-size: 14px;
    padding: 6px 8px;
  }
  
  .bulk-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .bulk-actions button {
    width: 100%;
    font-size: 13px;
  }
  
  .approval-stats {
    flex-direction: column;
    gap: 8px;
  }
  
  .articles-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .approval-summary-mini {
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }

  #centerdiv {
    margin: 16px 8px 20px 8px;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }

  .containerdiv {
    width: 100%;
    margin: 0;
    padding: 8px;
  }

  .articles-table-container {
    overflow-x: auto;
  }

  .articles-table {
    min-width: 600px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 2px;
  }

  .action-buttons button {
    width: 100%;
    font-size: 10px;
    padding: 3px 6px;
  }
}
</style>